import React, { Component } from 'react';
import { ReCaptcha, loadReCaptcha } from 'react-recaptcha-v3';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { gettext, dangerousGettext } from '@eventbrite/i18n';

import { EmailField } from '@eventbrite/auth-lib';
import { Icon } from '@eventbrite/eds-icon';
import { Button, SIZE_BLOCK } from '@eventbrite/eds-button';
import { withMainControls } from '@eventbrite/eds-structure';
import { Typography } from '@eventbrite/marmalade';
import { Link } from '@eventbrite/eds-link';
import { NotificationBar, TYPE_ERROR } from '@eventbrite/eds-notification';
import { logEvent } from '@eventbrite/statsig';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { sdkRequest } from '@eventbrite/http';

import MfaEnrolmentPage from './MfaEnrolmentPage';

import {
    DATA_HEAP_ID_LOGIN_WITH_APPLE_BUTTON,
    DATA_HEAP_ID_LOGIN_WITH_FACEBOOK_BUTTON,
    DATA_HEAP_ID_LOGIN_WITH_GOOGLE_BUTTON,
    HEAP_SOCIAL_LOGIN_EVENT,
    HEAP_FIND_MY_TICKETS_EVENT,
} from '@eventbrite/social-login-facebook';

import FormSubmitButton from '../../components/FormSubmitButton';
import AppleIconSvg from './icons/AppleIcon';
import FacebookIconSvg from './icons/FacebookIcon';
import GoogleIconSvg from './icons/GoogleIcon';

import { errorMessages } from '../../constants/errorMessages';
import {
    CAPTCHA_RETRY_RATE,
    CAPTCHA_POLL_RATE,
    CAPTCHA_MAX_WAIT_TIME,
    PRE_AUTH_PATH,
    MFA_ENROLLMENT_PATH,
} from '../../constants';

import './preAuthPage.scss';

import {
    HeapTrackingLibrary,
    HEAP_PASSWORDLESS_EMAIL_ATTEMPT,
    LOGIN_METHOD_FACEBOOK,
    LOGIN_METHOD_GOOGLE,
    LOGIN_METHOD_APPLE,
    LOGIN_PAGE_VIEW,
} from '@eventbrite/auth-lib/';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/SIGN_UP_PATH
 */

const ChangeEmailSchema = Yup.object().shape({
    email: Yup.string()
        .email(gettext('Please enter a valid email to proceed').toString())
        .required(gettext('Please enter an email to proceed').toString()),
});

const shouldShowMfaEnrollment = (response) => {
    const isOrganizer = response?.is_organizer;
    const skipTime = response?.user_metadata?.skip_screen_prompted_at;
    const mfaEnabledForPassword = response?.mfa_enabled_for_password;

    // If feature gate is disabled, don't show MFA enrollment
    if (!mfaEnabledForPassword) return false;

    if (!isOrganizer) return false;

    if (!skipTime) return true; // Never prompted before

    const now = Date.now();
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

    return now - skipTime > sevenDaysInMs;
};

class PreAuthPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            errorMessage:
                new URLSearchParams(this.props.location.search).get('error') ??
                '',
            isLoading: false,
            initialValues: {
                email:
                    new URLSearchParams(this.props.location.search).get(
                        'email',
                    ) ?? '',
            },
            findMyTicketsQueryParams: {
                email:
                    new URLSearchParams(this.props.location.search).get(
                        'email',
                    ) ?? '',
            },
            captchaToken: null,
            enableCaptchaV3: false,
            showCaptchaV3:
                new URLSearchParams(this.props.location.search).get(
                    'gating_error',
                ) === 'VERIFY_CAPTCHA',
            emailForPasswordless:
                new URLSearchParams(this.props.location.search).get(
                    'gating_email',
                ) ?? '',
            // New state for MFA enrollment flow
            showMfaEnrollment: false,
            currentEmail: '',
        };
    }

    redirectToAuth0 = (connectionType, email, isNewAccount = false) => {
        const connectionTypeValue = encodeURIComponent(connectionType);
        const emailValue = email ? `&email=${encodeURIComponent(email)}` : '';

        const queryString = this.props.location.search;
        const queryParams = new URLSearchParams(queryString);

        const referrerValue = queryParams.get('referrer')
            ? `&referrer=${encodeURIComponent(queryParams.get('referrer'))}`
            : '';

        const universalLoginValue = queryParams.get('universal_login')
            ? `&universal_login=${queryParams.get('universal_login')}`
            : '';

        const isNewAccountValue = isNewAccount ? '&new_account=true' : '';

        const recaptchaTokenValue = this.state.captchaToken
            ? `&recaptcha_v3_response_token=${encodeURIComponent(
                  this.state.captchaToken,
              )}`
            : '';

        const authUrl = `/api/v3/pre_auth/?connection=${connectionTypeValue}${emailValue}${referrerValue}${universalLoginValue}${recaptchaTokenValue}${isNewAccountValue}`;

        if (this.state.showCaptchaV3) {
            this.setState({ enableCaptchaV3: true });
            this.captchaStartTime = Date.now();

            const submitIfCaptcha = () => {
                if (
                    this.state.captchaToken ||
                    Date.now() >
                        this.captchaStartTime + CAPTCHA_MAX_WAIT_TIME ||
                    !this.props.disableRecaptcha3
                ) {
                    window.open(authUrl, '_self');
                } else {
                    this._submitIfCaptchaTimeout = setTimeout(
                        submitIfCaptcha,
                        CAPTCHA_POLL_RATE,
                    );
                }
            };

            submitIfCaptcha();
        } else {
            window.open(authUrl, '_self');
        }
    };

    interceptOnSubmit = async (values) => {
        try {
            this.setState({ isLoading: true });
            const { lookupUserAction } = this.props;
            await lookupUserAction({ email: values.email });
        } catch (error) {
            this.setState({ isLoading: false });
        }

        // if auth0RedirectUrl is present, user is an employee
        if (this.props.auth0RedirectUrl) {
            this.setState({ isLoading: false });
            window.open(this.props.auth0RedirectUrl, '_self');
        } else if (this.props.isSoftAccount || this.props.isSocialUser) {
            this.trackPasswordlessEmailEvents('auth0-login');
            this.redirectToAuth0('email', values.email);
        } else if (this.props.isNewAccount) {
            this.trackPasswordlessEmailEvents('auth0-signup');
            this.redirectToAuth0('email', values.email, true);
        } else {
            // Check if existing user with password
            // Check if user is a creator (only if feature flag is enabled)
            const shouldShowMfaEnrollment = await this.checkIfUserIsCreator(
                values.email,
            );

            if (shouldShowMfaEnrollment) {
                // User is eligible for MFA enrollment, now check if they have authentication methods
                const hasAuthenticationMethods =
                    await this.checkUserAuthenticationMethods(values.email);

                if (!hasAuthenticationMethods) {
                    // User is eligible and doesn't have authentication methods - show enrollment screen
                    this.setState({
                        showMfaEnrollment: true,
                        currentEmail: values.email,
                        isLoading: false,
                    });
                    const newUrl = `${MFA_ENROLLMENT_PATH}?email=${encodeURIComponent(
                        values.email,
                    )}&from_mfa_enrollment=true`;
                    window.history.pushState(
                        { showMfaEnrollment: true, currentEmail: values.email },
                        '',
                        newUrl,
                    );
                } else {
                    // User is eligible but already has authentication methods - continue existing flow
                    this.redirectToAuth0('eventbrite-cerberus', values.email);
                }
            } else {
                // User is not eligible for MFA enrollment - continue existing flow
                this.redirectToAuth0('eventbrite-cerberus', values.email);
            }
        }
    };

    trackPasswordlessEmailEvents = (pagePath) => {
        HeapTrackingLibrary.trackPasswordlessEmail({
            eventName: HEAP_PASSWORDLESS_EMAIL_ATTEMPT,
            pagePath,
        });
        logEvent(HEAP_PASSWORDLESS_EMAIL_ATTEMPT, null, {
            pagePath,
        });
    };

    redirectToAuth0WithMfa = (
        connectionType,
        email,
        isNewAccount = false,
        mfaEnrollmentRequired = false,
    ) => {
        const connectionTypeValue = encodeURIComponent(connectionType);
        const emailValue = email ? `&email=${encodeURIComponent(email)}` : '';

        const queryString = this.props.location.search;
        const queryParams = new URLSearchParams(queryString);

        const referrerValue = queryParams.get('referrer')
            ? `&referrer=${encodeURIComponent(queryParams.get('referrer'))}`
            : '';

        const universalLoginValue = queryParams.get('universal_login')
            ? `&universal_login=${queryParams.get('universal_login')}`
            : '';

        const isNewAccountValue = isNewAccount ? '&new_account=true' : '';

        const recaptchaTokenValue = this.state.captchaToken
            ? `&recaptcha_v3_response_token=${encodeURIComponent(
                  this.state.captchaToken,
              )}`
            : '';

        const mfaEnrollmentValue = mfaEnrollmentRequired
            ? '&mfa_enrollment_required=true'
            : '&mfa_enrollment_required=false';

        const authUrl = `/api/v3/pre_auth/?connection=${connectionTypeValue}${emailValue}${referrerValue}${universalLoginValue}${recaptchaTokenValue}${isNewAccountValue}${mfaEnrollmentValue}`;

        if (this.state.showCaptchaV3) {
            this.setState({ enableCaptchaV3: true });
            this.captchaStartTime = Date.now();

            const submitIfCaptcha = () => {
                if (
                    this.state.captchaToken ||
                    Date.now() >
                        this.captchaStartTime + CAPTCHA_MAX_WAIT_TIME ||
                    !this.props.disableRecaptcha3
                ) {
                    window.open(authUrl, '_self');
                } else {
                    this._submitIfCaptchaTimeout = setTimeout(
                        submitIfCaptcha,
                        CAPTCHA_POLL_RATE,
                    );
                }
            };

            submitIfCaptcha();
        } else {
            // Use pushState to maintain browser history for back button functionality
            window.history.pushState({ redirecting: true }, '', authUrl);
            window.open(authUrl, '_self');
        }
    };

    handleMfaEnrollmentContinue = () => {
        // Continue with MFA enrollment - call preauth with email connection and mfa_enrollment_required=true
        this.redirectToAuth0WithMfa(
            'email',
            this.state.currentEmail,
            false,
            true,
        );
    };

    handleMfaEnrollmentSkip = async () => {
        // Skip MFA enrolment - send parameter to track skip action
        // After skipping, check if user should see enrollment again based on eligibility
        this.redirectToAuth0WithMfa(
            'eventbrite-cerberus',
            this.state.currentEmail,
            false,
            false, // mfaEnrollmentRequired = false to track skip
        );
    };

    checkIfUserIsCreator = async (email) => {
        // This method is only called when feature flag is enabled
        try {
            const encodedEmail = encodeURIComponent(email);
            const response = await sdkRequest(
                `/api/v3/user_organizer_mfa_status/?email=${encodedEmail}`,
            );
            const shouldShow = shouldShowMfaEnrollment(response);
            return shouldShow;
        } catch (error) {
            // If API call fails, default to not showing MFA enrollment
            return false;
        }
    };

    checkUserAuthenticationMethods = async (email) => {
        try {
            const encodedEmail = encodeURIComponent(email);
            const response = await sdkRequest(
                `/api/v3/user_authentication_methods/?email=${encodedEmail}`,
            );
            return response?.has_authentication_methods || false;
        } catch (error) {
            // If API call fails, default to having authentication methods (continue existing flow)
            return true;
        }
    };

    _handleCaptchaV3 = (captchaToken) => {
        this.setState({
            enableCaptchaV3: false,
            captchaToken,
        });

        this.props.change('recaptchaV3ResponseToken', captchaToken);

        //The recaptcha token expires after two minutes
        this._handleCaptchaV3Timeout = setTimeout(() => {
            this.setState({ enableCaptchaV3: true });
        }, CAPTCHA_RETRY_RATE);
    };

    handlePopState = async (event) => {
        // Check if we're coming back to the MFA enrollment state
        const urlParams = new URLSearchParams(window.location.search);
        const emailFromUrl = urlParams.get('email');
        const fromMfaEnrollment = urlParams.get('from_mfa_enrollment');
        const isPasswordAddSecurityPath =
            window.location.pathname === MFA_ENROLLMENT_PATH;

        if (
            emailFromUrl &&
            fromMfaEnrollment === 'true' &&
            isPasswordAddSecurityPath
        ) {
            // Restore MFA enrollment state when coming back from MFA enrollment flow
            this.setState({
                showMfaEnrollment: true,
                currentEmail: emailFromUrl,
                isLoading: false, // Reset loading state when coming back
            });
        } else {
            // Reset to email input form
            this.setState({
                showMfaEnrollment: false,
                currentEmail: '',
                isLoading: false,
            });
        }
    };

    componentDidMount() {
        if (this.state.showCaptchaV3 && !this.props.disableRecaptcha3) {
            loadReCaptcha(this.props.recaptcha3PublicKey);
        }

        if (this.state.emailForPasswordless) {
            this.props.sendPasswordlessEmailAction?.({
                email: this.state.emailForPasswordless,
            });

            this.setState({ errorMessage: 'CONFIRM_EMAIL' });
        }

        // Check if user is coming back from MFA enrollment (e.g., after refresh)
        // If they have an email in URL params, check if they should show MFA enrollment
        const urlParams = new URLSearchParams(this.props.location.search);
        const emailFromUrl = urlParams.get('email');
        const fromMfaEnrollment = urlParams.get('from_mfa_enrollment');

        if (emailFromUrl && fromMfaEnrollment === 'true') {
            // Check MFA enrollment when coming back from MFA enrollment flow
            // This will be handled by handlePopState when the component mounts
        }

        if (!this.props.auth0RedirectUrl) {
            // Add popstate listener to handle browser back button
            this.handlePopState = this.handlePopState.bind(this);
            window.addEventListener('popstate', this.handlePopState);
        }

        logEvent(LOGIN_PAGE_VIEW, null, {
            Path: PRE_AUTH_PATH,
            Flow: 'auth0-login',
        });
    }

    componentWillUnmount() {
        if (this._submitIfCaptchaTimeout) {
            clearTimeout(this._submitIfCaptchaTimeout);
            this._submitIfCaptchaTimeout = null;
        }

        if (this._handleCaptchaV3Timeout) {
            clearTimeout(this._handleCaptchaV3Timeout);
            this._handleCaptchaV3Timeout = null;
        }

        // Remove popstate listener, employee flows
        if (this.handlePopState && !this.props.auth0RedirectUrl) {
            window.removeEventListener('popstate', this.handlePopState);
        }
    }

    render() {
        const {
            isLoading,
            initialValues,
            errorMessage,
            enableCaptchaV3,
            showCaptchaV3,
            showMfaEnrollment,
            currentEmail,
        } = this.state;
        const { text, url } = errorMessages[errorMessage] || {
            text: 'An unexpected error occurred.',
            url: '',
        };

        // If showing MFA enrollment, render the MFA enrollment component
        if (showMfaEnrollment) {
            return (
                <MfaEnrolmentPage
                    email={currentEmail}
                    onContinue={this.handleMfaEnrollmentContinue}
                    onSkip={this.handleMfaEnrollmentSkip}
                />
            );
        }

        // Otherwise render the email input form
        return (
            <div className="eds-structure eds-structure--min-height preAuth__page-bg eds-align--center">
                <div className="preAuth__box">
                    {errorMessage.length > 0 && (
                        <div className="eds-l-mar-bot-4">
                            <NotificationBar
                                type={TYPE_ERROR}
                                hasCloseButton={true}
                                onClose={() =>
                                    this.setState({ errorMessage: '' })
                                }
                            >
                                <Typography
                                    as="p"
                                    color="white"
                                    variant="body-md"
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: dangerousGettext(text, {
                                            link: url,
                                        }).toString(),
                                    }}
                                />
                            </NotificationBar>
                        </div>
                    )}
                    <img
                        src="https://cdn.evbstatic.com/s3-build/perm_001/89f846/django/images/email/base/big-brand.png"
                        alt="Brand logo"
                        width={125}
                        height={20}
                    />
                    <h1 className="eds-l-mar-top-10 preAuth__page-title">
                        {gettext('Welcome!')}
                    </h1>
                    <h1 className=" eds-l-mar-bot-4 preAuth__page-title">
                        {gettext("What's your email?")}
                    </h1>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={ChangeEmailSchema}
                        onSubmit={this.interceptOnSubmit}
                    >
                        {({ values }) => (
                            <>
                                <Form data-testid="form-login">
                                    <div className="eds-l-mar-bot-10 eds-l-mar-top-10 preAuth__email-field">
                                        <Field
                                            name="email"
                                            component={EmailField}
                                            value={values.email}
                                            shortLabel
                                            autoFocus={true}
                                            bottomSpacing={2}
                                            onKeyUp={(event) => {
                                                this.setState({
                                                    isLoading: false,
                                                    findMyTicketsQueryParams: {
                                                        email: event.value,
                                                    },
                                                    enableCaptchaV3: true,
                                                });
                                            }}
                                        />
                                    </div>
                                    {enableCaptchaV3 &&
                                        showCaptchaV3 &&
                                        !this.props.disableRecaptcha3 && (
                                            <ReCaptcha
                                                sitekey={
                                                    this.props
                                                        .recaptcha3PublicKey
                                                }
                                                action="pre_auth"
                                                verifyCallback={
                                                    this._handleCaptchaV3
                                                }
                                            />
                                        )}

                                    <div
                                        className="eds-l-mar-top-4 eds-l-mar-bot-6"
                                        data-testid="pre-auth-email-submit-button"
                                    >
                                        <FormSubmitButton
                                            buttonClasses="preAuth__btn"
                                            type="submit"
                                            isLoading={isLoading}
                                            buttonCTA={gettext('Continue')}
                                        />
                                    </div>
                                </Form>
                            </>
                        )}
                    </Formik>
                    <div className="preAuth__divider eds-l-pad-bot-3 eds-l-pad-top-2">
                        <div className="preAuth__divider-text eds-text-bm eds-text-weight--heavy">
                            {gettext('Or sign in with')}
                        </div>
                    </div>
                    <div className="eds-l-mar-top-4 eds-l-mar-bot-4 eds-align--center eds-text--center">
                        <Typography
                            className="preAuth__secondary-text"
                            as="p"
                            color="neutral-600"
                            variant="body-sm"
                            dangerouslySetInnerHTML={{
                                __html: dangerousGettext(
                                    'By clicking Continue or the Apple, Google, or Facebook icons, you agree to Eventbrite\'s <a href="%(termsUrl)s" target="_blank">Terms of Service</a> and <a href="%(privacyUrl)s" target="_blank">Privacy Policy</a>.',
                                    {
                                        termsUrl: '/tos',
                                        privacyUrl: '/privacypolicy',
                                    },
                                ).toString(),
                            }}
                        />
                    </div>
                    <div className="eds-align--center eds-l-pad-left-6 eds-l-pad-right-6 eds-l-mar-top-10 eds-l-mar-bot-8">
                        <Button
                            data-heap-id={DATA_HEAP_ID_LOGIN_WITH_APPLE_BUTTON}
                            data-testid="pre-auth-apple-login-button"
                            type="button"
                            size={SIZE_BLOCK}
                            onClick={() => {
                                HAS_WINDOW &&
                                    window?.dataLayer.push({
                                        event: 'track',
                                        sendToHeap: true,
                                        eventName: HEAP_SOCIAL_LOGIN_EVENT,
                                        eventData: {
                                            platform: 'Auth0',
                                            label: 'Apple',
                                        },
                                    });
                                logEvent(HEAP_SOCIAL_LOGIN_EVENT, null, {
                                    label: LOGIN_METHOD_APPLE,
                                });
                                this.redirectToAuth0('Apple-Connection');
                            }}
                            style="none"
                        >
                            <Icon
                                type={<AppleIconSvg />}
                                isBlockLevel={true}
                                size="medium"
                            />
                            <div className="eds-is-hidden-accessible">
                                {gettext('Sign in with Apple')}
                            </div>
                        </Button>
                        <Button
                            data-heap-id={DATA_HEAP_ID_LOGIN_WITH_GOOGLE_BUTTON}
                            data-testid="pre-auth-google-login-button"
                            type="button"
                            size={SIZE_BLOCK}
                            onClick={() => {
                                HAS_WINDOW &&
                                    window?.dataLayer.push({
                                        event: 'track',
                                        sendToHeap: true,
                                        eventName: HEAP_SOCIAL_LOGIN_EVENT,
                                        eventData: {
                                            platform: 'Auth0',
                                            label: 'Google',
                                        },
                                    });
                                logEvent(HEAP_SOCIAL_LOGIN_EVENT, null, {
                                    label: LOGIN_METHOD_GOOGLE,
                                });
                                this.redirectToAuth0(
                                    'Google-OAuth2-Connection',
                                );
                            }}
                            style="none"
                        >
                            <Icon
                                type={<GoogleIconSvg />}
                                isBlockLevel={true}
                                size="medium"
                            />
                            <div className="eds-is-hidden-accessible">
                                {gettext('Sign in with Google')}
                            </div>
                        </Button>
                        <Button
                            data-heap-id={
                                DATA_HEAP_ID_LOGIN_WITH_FACEBOOK_BUTTON
                            }
                            data-testid="pre-auth-facebook-login-button"
                            type="button"
                            size={SIZE_BLOCK}
                            onClick={() => {
                                HAS_WINDOW &&
                                    window?.dataLayer.push({
                                        event: 'track',
                                        sendToHeap: true,
                                        eventName: HEAP_SOCIAL_LOGIN_EVENT,
                                        eventData: {
                                            platform: 'Auth0',
                                            label: 'Facebook',
                                        },
                                    });
                                logEvent(HEAP_SOCIAL_LOGIN_EVENT, null, {
                                    label: LOGIN_METHOD_FACEBOOK,
                                });
                                this.redirectToAuth0('Facebook-Connection');
                            }}
                            style="none"
                        >
                            <Icon
                                type={<FacebookIconSvg />}
                                isBlockLevel={true}
                                size="medium"
                            />
                            <div className="eds-is-hidden-accessible">
                                {gettext('Sign in with Facebook')}
                            </div>
                        </Button>
                    </div>
                    <div className="eds-align--center eds-l-pad-top-4 preAuth__secondary-text-mytickets">
                        <Link
                            type="button"
                            data-testid="pre-auth-find-my-tickets"
                            to={`/find-tickets/${
                                this.state.findMyTicketsQueryParams.email
                                    ? `?email=${encodeURIComponent(
                                          this.state.findMyTicketsQueryParams
                                              .email,
                                      )}`
                                    : ''
                            }`}
                            onClick={() => {
                                HAS_WINDOW &&
                                    window?.dataLayer.push({
                                        event: 'track',
                                        sendToHeap: true,
                                        eventName: HEAP_FIND_MY_TICKETS_EVENT,
                                        eventData: {
                                            platform: 'Auth0',
                                            referrer: '/pre-auth',
                                        },
                                    });
                                logEvent(HEAP_FIND_MY_TICKETS_EVENT);
                            }}
                        >
                            {gettext('Need help finding your tickets?')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }
}

export default withMainControls(PreAuthPage);
